import Model, { attr, hasMany } from '@warp-drive/legacy/model';
import { Type } from '@warp-drive/core-types/symbols';
import type AuditLogModel from './audit-log.ts';

/**
 * Canonical TAS audit worklist row (tio_tas.audit_queues), read via
 * `GET /api/v2/audit-queues/:id` (Api::V2::AuditQueueResource).
 *
 * Backs the audit detail / write pane (tio-front-end#8797) so a read-after-write
 * is a consistent DB read, independent of the audit_queue search index. The
 * worklist *list* still reads the index (see hitToItem in the route).
 */
export default class AuditQueueModel extends Model {
  // Stored columns.
  @attr declare decidedAt: string;
  @attr declare decidedBy: 'system' | 'person';
  @attr declare actionTaken: string;
  @attr declare auditFlag: string;
  @attr declare auditReason: string | null;
  @attr declare auditStatus: 'open' | 'escalated' | 'closed' | null;
  @attr declare markedForAudit: boolean;
  @attr declare devNote: string | null;
  @attr declare requestedAmountCents: number | null;
  @attr declare robotApprovedAmountCents: number | null;
  @attr declare personApprovedAmountCents: number | null;
  @attr declare availableFundsCents: number | null;
  // Decimal(4,2) — serialized as a string to preserve precision.
  @attr declare robotConfidenceLevel: string | null;
  @attr declare logTransitionId: string;
  @attr declare tasApplicationId: string;
  @attr declare companyId: string;
  @attr declare aiRecommendationId: string | null;
  @attr declare createdAt: string;
  @attr declare updatedAt: string;

  // Denormalized display + audit-trail facts, derived server-side and shared
  // with the search projection so the detail read and the worklist row agree.
  @attr declare applicantName: string | null;
  @attr declare companyNameDisplay: string | null;
  @attr declare programNameDisplay: string | null;
  @attr declare applicationName: string | null;
  @attr declare robotRecAction: string | null;
  @attr declare auditLogPresent: boolean;
  @attr declare escalationRequested: boolean;
  @attr declare auditedBy: string | null;
  @attr declare auditedAt: string | null;

  // The audit trail, oldest first (?include=audit-logs): the auditor's entry
  // (advisory opinion) then, once resolved, the reviewer's escalation_review.
  @hasMany('audit-log', { async: false, inverse: null })
  declare auditLogs: AuditLogModel[];

  [Type] = 'audit-queue' as const;
}
